import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import LeadForm from '@/components/LeadForm';
import SEO from '@/components/SEO';
import { API_URL } from '@/lib/utils';
import { Check, ArrowRight, Phone, Cloud, Star, Shield, Building, ChevronDown, ChevronUp } from 'lucide-react';

const CAREO_LOGO = "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/di9kfck2_53561592_transparent11%20-%20Copy.png";

const iconMap = {
  'SAAS BASED': Cloud,
  'SPECIALTY SAAS': Star,
  'INSURANCE & CLAIMS': Shield,
  'ENTERPRISE HOSPITAL': Building,
};

const colorMap = {
  0: { border: 'border-l-4 border-l-emerald-400', tag: 'bg-emerald-50 text-emerald-700', btn: 'bg-[#1a2a5e] hover:bg-[#2d4a8e]' },
  1: { border: 'border-l-4 border-l-teal-400', tag: 'bg-teal-50 text-teal-700', btn: 'bg-teal-600 hover:bg-teal-700' },
  2: { border: 'border-l-4 border-l-amber-400', tag: 'bg-amber-50 text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' },
  3: { border: '', tag: 'bg-amber-100 text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700', highlighted: true },
};

const ProductCard = ({ product, index }) => {
  const [expanded, setExpanded] = useState(false);
  const style = colorMap[index % 4] || colorMap[0];
  const isHighlighted = product.is_highlighted || style.highlighted;
  const Icon = iconMap[product.category_tag?.toUpperCase()] || Cloud;

  const visibleFeatures = expanded ? product.features : product.features?.slice(0, 8);
  const hasMore = product.features?.length > 8;

  return (
    <div
      className={`rounded-2xl p-6 md:p-8 transition-all hover:shadow-lg ${
        isHighlighted
          ? 'bg-gradient-to-br from-[#1a2a5e] to-[#2d4a8e] text-white'
          : `bg-white border ${style.border}`
      }`}
      data-testid={`product-card-${product.slug}`}
    >
      {/* Category Tag */}
      {product.category_tag && (
        <div className="mb-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
            isHighlighted ? 'bg-white/20 text-white' : style.tag
          }`}>
            <Icon size={12} />
            {product.category_tag}
          </span>
        </div>
      )}

      {/* Name */}
      <h2 className={`font-manrope text-2xl md:text-3xl font-bold mb-1 ${isHighlighted ? 'text-white' : 'text-[#1a2a5e]'}`}>
        {product.name}
      </h2>

      {/* Subtitle/Tagline */}
      {(product.subtitle || product.tagline) && (
        <p className={`text-sm font-medium mb-4 ${isHighlighted ? 'text-amber-300' : 'text-[#2eb24c]'}`}>
          {product.subtitle || product.tagline}
        </p>
      )}

      {/* Description */}
      <p className={`text-sm leading-relaxed mb-5 ${isHighlighted ? 'text-blue-100' : 'text-gray-600'}`}>
        {product.description}
      </p>

      {/* Features Grid */}
      {product.features?.length > 0 && (
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {visibleFeatures.map((feature, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <Check size={14} className={`mt-0.5 flex-shrink-0 ${isHighlighted ? 'text-amber-300' : 'text-[#2eb24c]'}`} />
                <span className={isHighlighted ? 'text-blue-50' : 'text-gray-700'}>{feature}</span>
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className={`flex items-center gap-1 text-xs font-medium mt-3 ${isHighlighted ? 'text-amber-300 hover:text-amber-200' : 'text-[#1a2a5e] hover:text-[#2d4a8e]'}`}
              data-testid={`expand-features-${product.slug}`}
            >
              {expanded ? 'Show Less' : 'Explore More'}
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="flex flex-wrap gap-3">
        {product.button_text && (
          <Button
            asChild
            className={isHighlighted ? 'bg-amber-500 hover:bg-amber-600 text-white' : style.btn + ' text-white'}
          >
            {product.button_link ? (
              <a href={product.button_link} target="_blank" rel="noopener noreferrer">
                {product.button_text} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            ) : (
              <a href="#demo-form">
                {product.button_text} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            )}
          </Button>
        )}
        {product.secondary_button_text && (
          <Button
            asChild
            variant="outline"
            className={isHighlighted ? 'border-white text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}
          >
            {product.secondary_button_link ? (
              <a href={product.secondary_button_link} target="_blank" rel="noopener noreferrer">
                {product.secondary_button_text}
              </a>
            ) : (
              <Link to="/contact">{product.secondary_button_text}</Link>
            )}
          </Button>
        )}
      </div>

      {/* Pricing note */}
      {product.pricing && isHighlighted && (
        <p className="text-xs text-blue-200 mt-4 italic">{product.pricing}</p>
      )}
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div data-testid="products-page">
      <SEO
        title="Digital Products/EMR - Healthcare Software Solutions"
        description="Explore CareO suite of healthcare software products including EMR, HIS, CMS, and specialty solutions."
        keywords="CareO EMR, hospital information system, healthcare software, digital health products India"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1a2a5e] to-[#3b7dd8] py-16 md:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <img src={CAREO_LOGO} alt="CareO" className="h-16 mx-auto mb-6 brightness-0 invert" />
          <h1 className="font-manrope text-4xl md:text-5xl font-bold mb-4">
            Digital Products / EMR
          </h1>
          <p className="text-blue-100 text-lg max-w-3xl mx-auto">
            Complete suite of healthcare software solutions — from cloud-based EMR for clinics to enterprise HIS for multi-specialty hospitals
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading products...</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8" data-testid="products-grid">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Demo Form */}
      <section id="demo-form" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="font-manrope text-3xl font-bold text-[#1a2a5e] mb-3">Request a Demo</h2>
            <p className="text-gray-600">Fill out the form and our team will schedule a personalized demo for you</p>
          </div>
          <LeadForm source="products_demo" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl font-bold mb-4">Ready to Transform Your Healthcare Facility?</h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Our expert team is ready to help you choose and implement the right solution
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40]">
              <a href="#demo-form">Schedule Demo</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#1a2a5e]">
              <a href="tel:+919616298162">
                <Phone className="mr-2 h-5 w-5" />
                +91 9616298162
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
